import { makeAutoObservable, reaction } from "mobx";
import { RootStore } from "../../store/RootStore";

export interface FileEditorConfig {
  // Identificación
  entityType?: string;
  entityId?: string;
  fieldName?: string;

  // Display
  title?: string;

  // Contenido
  content?: string;
  loader?: () => Promise<string>;

  // Comportamiento
  readOnly?: boolean;
  onSave?: (content: string, metadata: any) => Promise<void>;
  onValidate?: (content: string) => string | null;
  onChange?: (content: string) => void;
  onCancel?: () => void;

  // Metadata adicional
  metadata?: any;
}

export class FileEditorStore {
  isOpen = false;
  isLoading = false;
  readOnly = false;
  content = "";
  originalContent = "";
  error: string | null = null;

  // Metadata flexible
  metadata: {
    entityType?: string;
    entityId?: string;
    fieldName?: string;
    displayName?: string;
    context?: any;
  } = {};

  // Estrategias pluggables
  private loader?: () => Promise<string>;
  private saver?: (content: string, metadata: any) => Promise<void>;
  private validator?: (content: string) => string | null;
  private changeHandler?: (content: string) => void;
  private cancelHandler?: () => void;

  private disposeReaction?: () => void;

  constructor(_rootStore: RootStore) {
    // _rootStore disponible para uso futuro si se necesita
    makeAutoObservable(this);

    this.disposeReaction = reaction(
      () => this.content,
      (content) => {
        if (this.changeHandler) {
          this.changeHandler(content);
        }
        if (this.validator) {
          this.error = this.validator(content);
        }
      }
    );
  }

  async open(config: FileEditorConfig) {
    // Reset state
    this.error = null;
    this.readOnly = config.readOnly ?? false;

    // Store metadata
    this.metadata = {
      entityType: config.entityType,
      entityId: config.entityId,
      fieldName: config.fieldName,
      displayName: config.title,
      context: config.metadata,
    };

    // Store strategies
    this.loader = config.loader;
    this.saver = config.onSave;
    this.validator = config.onValidate;
    this.changeHandler = config.onChange;
    this.cancelHandler = config.onCancel;

    // Load content
    if (config.content !== undefined) {
      this.content = config.content;
      this.originalContent = config.content;
      this.isOpen = true;
    } else if (this.loader) {
      this.isLoading = true;
      try {
        const loadedContent = await this.loader();
        this.content = loadedContent;
        this.originalContent = loadedContent;
        this.isOpen = true;
      } catch (error) {
        this.error = `Error cargando contenido: ${error}`;
        this.isOpen = true;
      } finally {
        this.isLoading = false;
      }
    } else {
      this.content = "";
      this.originalContent = "";
      this.isOpen = true;
    }
  }

  setContent(content: string) {
    this.content = content;
  }

  get hasChanges() {
    return this.content !== this.originalContent;
  }

  get canSave() {
    return this.hasChanges && !this.error && !this.isLoading;
  }

  async save() {
    if (!this.canSave) return;

    if (this.saver) {
      try {
        this.isLoading = true;
        await this.saver(this.content, this.metadata);
        this.close();
      } catch (error) {
        this.error = `Error guardando: ${error}`;
      } finally {
        this.isLoading = false;
      }
    } else {
      this.close();
    }
  }

  cancel() {
    if (this.cancelHandler) {
      this.cancelHandler();
    }
    this.close();
  }

  close() {
    this.isOpen = false;
    this.readOnly = false;
    this.content = "";
    this.originalContent = "";
    this.error = null;
    this.metadata = {};
    this.loader = undefined;
    this.saver = undefined;
    this.validator = undefined;
    this.changeHandler = undefined;
    this.cancelHandler = undefined;
  }

  destroy() {
    if (this.disposeReaction) {
      this.disposeReaction();
    }
  }
}
